import { useEffect, useState } from 'react'
import {
  fetchHealth,
  fetchMeasurements,
  saveSession,
  type HealthSummary,
  type Measurement,
  type TodayResponse,
} from '../lib/api'
import { ErrorBanner, Group, Row, Screen } from '../components/ui'
import { ActivityRings, RingLegend, type Ring } from '../components/rings'
import { IconCheck, IconMinus } from '../components/icons'

const PLAN_LABELS: Record<string, string> = {
  zaryadka: 'Зарядка 10 минут',
  gym: 'Зал',
  gym_a: 'Зал А',
  gym_b: 'Зал Б',
  run: 'Бег 5 км',
  intervals: 'Интервалы 20 минут',
  walk: 'Прогулка',
  rest: 'Отдых',
}

/**
 * Пункт «зал» в плане не уточняет, какой именно — А или Б, а сессия пишется
 * как gym_a или gym_b. Без этого разбора любая выполненная тренировка
 * отмечала весь день, включая зарядку и бег.
 */
function matches(list: string[], planKind: string): boolean {
  if (planKind === 'gym') return list.some((kind) => kind.startsWith('gym'))
  return list.includes(planKind)
}

type PlanState = 'open' | 'done' | 'skipped'

/**
 * Три состояния, а не два. Пустая строка означает «ещё не решил», и это не то
 * же самое, что «сегодня точно не буду»: раньше второе было нечем отметить, и
 * пропущенный день выглядел как забытый.
 */
function stateOf(today: TodayResponse, planKind: string): PlanState {
  if (matches(today.done, planKind)) return 'done'
  if (matches(today.skipped ?? [], planKind)) return 'skipped'
  // Часы засчитывают сами: пробежку и прогулку человеку отмечать незачем.
  if ((today.auto ?? []).some((item) => matches([item.kind], planKind))) return 'done'
  return 'open'
}

/** Откуда взялась отметка — нужно подписать, иначе «сделал» выглядит магией. */
function noteOf(today: TodayResponse, item: TodayResponse['plan'][number]): string | undefined {
  if (matches(today.skipped ?? [], item.kind)) return 'сегодня не буду'

  const auto = (today.auto ?? []).find((entry) => matches([entry.kind], item.kind))
  if (auto && !matches(today.done, item.kind)) {
    return `${auto.source === 'watch' ? 'часы' : 'по шагам'}: ${auto.detail}`
  }
  return item.extra ? 'сверх плана' : undefined
}

const NEXT_STATE: Record<PlanState, PlanState> = {
  open: 'done',
  done: 'skipped',
  skipped: 'open',
}

export function TodayScreen({ today, onChanged }: { today: TodayResponse; onChanged: () => void }) {
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [health, setHealth] = useState<HealthSummary | null>(null)
  const [measurements, setMeasurements] = useState<Measurement[]>([])

  // Сводка собирается из трёх источников: день, часы, замеры. Так человек
  // видит день целиком и не ходит за одной цифрой по вкладкам.
  useEffect(() => {
    void fetchHealth()
      .then(setHealth)
      .catch(() => setHealth(null))
    void fetchMeasurements()
      .then((data) => setMeasurements(data.measurements))
      .catch(() => setMeasurements([]))
  }, [])

  // Тап переключает по кругу: не решил → сделал → сегодня не буду → не решил.
  async function cycle(kind: string, current: PlanState) {
    const next = NEXT_STATE[current]
    setBusy(kind)
    setError(null)
    try {
      await saveSession({ kind, completed: next === 'done', skipped: next === 'skipped' })
      onChanged()
    } catch (cause) {
      setError(`Не удалось отметить: ${(cause as Error).message}`)
    } finally {
      setBusy(null)
    }
  }

  const steps = health?.summary.find((item) => item.metric === 'steps')?.today ?? null
  // Кольцо считает только плановые дела: отметка сверх плана не должна
  // поднимать планку — иначе за лишнюю прогулку кольцо становится дальше.
  const planned = today.plan.filter((item) => !item.extra)
  const planDone = planned.filter((item) => stateOf(today, item.kind) === 'done').length
  const latestWeight = measurements.find((row) => row.weight_kg !== null)?.weight_kg ?? null
  const previousWeight =
    measurements.filter((row) => row.weight_kg !== null)[1]?.weight_kg ?? null

  const rings: Ring[] = [
    {
      id: 'protein',
      label: 'Белок',
      value: today.eaten.proteinG,
      target: today.targets.proteinG,
      unit: 'г',
      color: 'var(--color-move)',
    },
    {
      id: 'plan',
      label: 'План',
      value: planDone,
      target: Math.max(1, planned.length),
      unit: planned.length === 1 ? 'дело' : 'дела',
      color: 'var(--color-exercise)',
    },
    {
      id: 'steps',
      label: 'Шаги',
      value: steps ?? 0,
      target: today.targets.steps,
      unit: '',
      color: 'var(--color-stand)',
      missing: steps === null,
    },
  ]

  const remaining = today.targets.kcal - today.eaten.kcal
  const over = remaining < 0
  const eatenShare = Math.min(100, Math.round((today.eaten.kcal / today.targets.kcal) * 100))

  return (
    <Screen title="Сегодня">
      <div className="-mt-4">
        <p className="footnote first-letter:uppercase">{formatDate(today.date)}</p>
      </div>

      <div className="flex items-center gap-5">
        <ActivityRings rings={rings} />
        <RingLegend rings={rings} />
      </div>

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      {/* Калории вынесены из колец: их цель обратная — не превысить, — и
          заполненное кольцо означало бы провал вместо успеха. */}
      <Group>
        <div className="px-4 py-3.5">
          <div className="flex items-end justify-between gap-3">
            <div>
              <div
                className="numerals text-[40px] font-semibold leading-none"
                style={over ? { color: 'var(--color-sys-orange)' } : undefined}
              >
                {Math.abs(remaining)}
              </div>
              <div className="footnote mt-1">
                {over ? 'сверх дневной нормы' : 'осталось на сегодня'}
              </div>
            </div>
            <div className="numerals pb-1 text-right text-[15px] text-ink-2">
              {today.eaten.kcal} / {today.targets.kcal}
            </div>
          </div>

          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-ink-fill">
            <div
              className="h-full rounded-full transition-[width] duration-500"
              style={{
                width: `${eatenShare}%`,
                background: over ? 'var(--color-sys-orange)' : 'var(--color-sys-green)',
              }}
            />
          </div>

        </div>
      </Group>

      <Group
        caption="План на сегодня"
        footnote={
          today.plan.length
            ? 'Тап переключает: сделал → сегодня не буду → пусто.'
            : 'На этот день план не задан.'
        }
      >
        {today.plan.map((item) => {
          const state = stateOf(today, item.kind)
          return (
            <Row
              key={item.kind}
              title={
                <span className={state === 'done' ? 'text-ink-2 line-through' : ''}>
                  {PLAN_LABELS[item.kind] ?? item.kind}
                </span>
              }
              subtitle={noteOf(today, item)}
              onPress={() => cycle(item.kind, state)}
              accessory={
                <span
                  aria-hidden="true"
                  className={`flex size-7 shrink-0 items-center justify-center rounded-full border ${
                    state === 'done'
                      ? 'border-transparent bg-sys-green text-black'
                      : state === 'skipped'
                        ? 'border-transparent bg-ink-fill text-ink-2'
                        : busy === item.kind
                          ? 'border-ink-fill text-ink-3'
                          : 'border-ink-fill text-transparent'
                  }`}
                >
                  {state === 'skipped' ? <IconMinus size={15} /> : <IconCheck size={15} />}
                </span>
              }
            />
          )
        })}
      </Group>

      {latestWeight !== null && (
        <Group caption="Тело">
          <Row
            title="Вес"
            subtitle={
              previousWeight !== null
                ? `с прошлого замера ${formatDelta(Number(latestWeight) - Number(previousWeight))}`
                : 'первый замер'
            }
            value={`${Number(latestWeight).toFixed(1)} кг`}
          />
        </Group>
      )}
    </Screen>
  )
}

function formatDelta(delta: number): string {
  const rounded = Math.round(delta * 10) / 10
  if (rounded === 0) return 'без изменений'
  return `${rounded > 0 ? '+' : ''}${rounded}`
}

function formatDate(isoDate: string): string {
  const date = new Date(`${isoDate}T00:00:00`)
  return new Intl.DateTimeFormat('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' }).format(
    date,
  )
}
